import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Autocomplete, Box, Chip, TextField, Typography, Paper,
    CircularProgress, Alert, Stack
} from '@mui/material';
import * as api from './subCategoriesApi';
import type { SubCategory, MainGroup, Intrastat } from './subCategoriesApi';
import { sx14, paperSx, FormRow, DetailHeader } from '../common/detailView';

interface Props {
    subCategoryId: number | null;
}

export function SubCategoryView({ subCategoryId }: Props) {
    const { t } = useTranslation();

    const [item, setItem] = useState<SubCategory | null>(null);
    const [mainGroups, setMainGroups] = useState<MainGroup[]>([]);
    const [intrastats, setIntrastats] = useState<Intrastat[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [copy, setCopy] = useState<SubCategory | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [mgs, intr] = await Promise.all([api.getMainGroups(), api.getIntrastats()]);
            setMainGroups(mgs);
            setIntrastats(intr);
            if (subCategoryId === null) {
                setItem({ id: 0, subCategoryCode: '', subCategoryName: '', mainGroups: [], intrastats: [] });
                setIsNew(true);
                setIsEditing(true);
            } else {
                const data = await api.getSubCategory(subCategoryId);
                data.mainGroups = data.mainGroups ?? [];
                data.intrastats = data.intrastats ?? [];
                setItem(data);
                setIsNew(false);
                setIsEditing(false);
            }
        } catch (err) {
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, [subCategoryId]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    if (error && !item) return <Alert severity="error">{error}</Alert>;
    if (!item) return null;

    const isCodeValid = !!item.subCategoryCode && item.subCategoryCode.length >= 1 && item.subCategoryCode.length <= 3;
    const isNameValid = !!item.subCategoryName;
    const canSave = isCodeValid && isNameValid;

    const startEdit = () => { setCopy(JSON.parse(JSON.stringify(item))); setIsEditing(true); };
    const cancelEdit = () => { if (copy) setItem(copy); setIsEditing(false); };

    const save = async () => {
        setSaving(true); setError(null);
        try {
            const response = await api.saveSubCategory(item);
            response.mainGroups = response.mainGroups ?? [];
            response.intrastats = response.intrastats ?? [];
            setItem(response);
            setIsNew(false);
            setIsEditing(false);
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const update = <K extends keyof SubCategory>(field: K, value: SubCategory[K]) => {
        setItem({ ...item, [field]: value });
    };

    return (
        <Paper variant="outlined" sx={paperSx}>
            <DetailHeader
                title={`${item.subCategoryCode ?? ''} ${item.subCategoryName ?? ''}`}
                isEditing={isEditing}
                isNew={isNew}
                saving={saving}
                canSave={canSave}
                onEdit={startEdit}
                onSave={save}
                onCancel={cancelEdit}
            />

            <Box p={2}>
                <Stack spacing={2}>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                    <FormRow label={t('subCategory.subCategoryCode')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{item.subCategoryCode}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                inputProps={{ maxLength: 3, minLength: 1 }}
                                value={item.subCategoryCode}
                                onChange={e => update('subCategoryCode', e.target.value)}
                                error={!isCodeValid}
                                helperText={!isCodeValid ? t('subCategory.betweenValues') : undefined} />
                        )}
                    </FormRow>

                    <FormRow label={t('subCategory.subCategoryName')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{item.subCategoryName}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                value={item.subCategoryName}
                                onChange={e => update('subCategoryName', e.target.value)}
                                error={!isNameValid}
                                helperText={!isNameValid ? t('subCategory.required') : undefined} />
                        )}
                    </FormRow>

                    <FormRow label={t('subCategory.maingroups')}>
                        {!isEditing ? (
                            <Box>
                                {[...item.mainGroups]
                                    .sort((a, b) => a.mainGroupCode.localeCompare(b.mainGroupCode))
                                    .map(mg => (
                                        <Typography sx={sx14} key={mg.id}>
                                            {mg.mainGroupCode} {mg.mainGroupDescription}
                                        </Typography>
                                    ))}
                            </Box>
                        ) : (
                            <Autocomplete
                                multiple
                                size="small"
                                options={[...mainGroups].sort((a, b) => a.mainGroupCode.localeCompare(b.mainGroupCode))}
                                getOptionLabel={o => `${o.mainGroupCode} ${o.mainGroupDescription}`}
                                value={item.mainGroups}
                                onChange={(_, value) => update('mainGroups', value)}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                renderInput={params => <TextField {...params} placeholder={t('subCategory.maingroups')} />}
                                renderTags={(value, getTagProps) =>
                                    value.map((mg, i) => (
                                        <Chip {...getTagProps({ index: i })} key={mg.id}
                                            label={`${mg.mainGroupCode} ${mg.mainGroupDescription}`} size="small" />
                                    ))
                                }
                            />
                        )}
                    </FormRow>

                    {intrastats.length > 0 && (
                        <FormRow label={t('maingroup.intrastatnumber')}>
                            {!isEditing ? (
                                <Box>
                                    {item.intrastats.map(i => (
                                        <Typography sx={sx14} key={i.id} title={i.internalDescription}>
                                            {i.code}
                                        </Typography>
                                    ))}
                                </Box>
                            ) : (
                                <Autocomplete
                                    multiple
                                    size="small"
                                    options={[...intrastats].sort((a, b) => a.code.localeCompare(b.code))}
                                    getOptionLabel={o => `${o.code} ${o.internalDescription}`}
                                    value={item.intrastats}
                                    onChange={(_, value) => update('intrastats', value)}
                                    isOptionEqualToValue={(o, v) => o.id === v.id}
                                    renderInput={params => <TextField {...params} />}
                                    renderTags={(value, getTagProps) =>
                                        value.map((i, idx) => (
                                            <Chip {...getTagProps({ index: idx })} key={i.id}
                                                label={`${i.code} ${i.internalDescription}`} size="small" />
                                        ))
                                    }
                                />
                            )}
                        </FormRow>
                    )}
                </Stack>
            </Box>

            {saving && <Box display="flex" justifyContent="center" p={1}><CircularProgress size={20} /></Box>}
        </Paper>
    );
}
